import { StandardEvents } from '@shopify/events';

/** Manual pagination. No observer or prefetch: requests begin only on click. */
class CotigoLoadMore extends HTMLElement {
  #request;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(StandardEvents.searchUpdate, this.#onFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#onFilterUpdate);
  }

  disconnectedCallback() {
    this.#cancel();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(StandardEvents.searchUpdate, this.#onFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#onFilterUpdate);
  }

  #cancel() {
    this.#request?.abort();
    this.#request = undefined;
    this.removeAttribute('aria-busy');
    const button = this.querySelector('[data-load-more-button]');
    if (button) {
      button.disabled = !this.dataset.nextUrl;
      button.textContent = this.dataset.nextUrl ? this.dataset.more : this.dataset.done;
    }
  }

  #onFilterUpdate = (event) => {
    const section = event.target.closest('[id^="shopify-section-"]');
    if (section?.id === `shopify-section-${this.dataset.sectionId}`) this.#cancel();
  };

  #onClick = async (event) => {
    const button = event.target.closest('[data-load-more-button]');
    if (!button || this.#request || !this.dataset.nextUrl) return;
    const results = this.closest('results-list');
    const grid = results?.querySelector('[ref="grid"]');
    const status = this.querySelector('[data-load-more-status]');
    if (!grid || !status) return;

    const request = new AbortController();
    this.#request = request;
    button.disabled = true;
    button.textContent = this.dataset.loading;
    this.setAttribute('aria-busy', 'true');

    try {
      // Retain current filters, sorting, search query, locale and draft preview.
      const next = new URL(this.dataset.nextUrl, window.location.href);
      const url = new URL(window.location.href);
      url.searchParams.set('page', next.searchParams.get('page'));
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.hash = '';
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Section request failed');
      const html = await response.text();
      if (request.signal.aborted || this.#request !== request || !this.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('cotigo-load-more');
      const incomingGrid = doc.querySelector('[ref="grid"]');
      if (!incoming || !incomingGrid) throw new Error('Missing results');

      const ids = new Set([...grid.querySelectorAll(':scope > [data-product-id]')].map(card => card.dataset.productId));
      const cards = [...incomingGrid.querySelectorAll(':scope > [ref="cards[]"]')].filter(card => !ids.has(card.dataset.productId));
      grid.append(...cards);
      results.updatedCallback?.();
      this.dataset.nextUrl = incoming.dataset.nextUrl;
      this.dataset.loaded = String(grid.querySelectorAll(':scope > [ref="cards[]"]').length);
      this.dataset.total = incoming.dataset.total;
      status.textContent = this.dataset.progress.replace('__count__', this.dataset.loaded).replace('__total__', this.dataset.total);
      button.textContent = this.dataset.nextUrl ? this.dataset.more : this.dataset.done;
      button.disabled = !this.dataset.nextUrl;
    } catch (error) {
      if (request.signal.aborted || this.#request !== request) return;
      status.textContent = this.dataset.error;
      button.textContent = this.dataset.more;
      button.disabled = false;
    } finally {
      if (this.#request === request) {
        this.#request = undefined;
        this.removeAttribute('aria-busy');
      }
    }
  };
}

if (!customElements.get('cotigo-load-more')) customElements.define('cotigo-load-more', CotigoLoadMore);
